import { useCallback, useEffect, useState } from "react";
import {
  canExtendSilently, extendSession, reauthenticate, sessionExpiresAt,
} from "../lib/auth";
import {
  flushBeforeExpiry, formatCountdown, msUntilExpiry, pendingFlushCount, pollIntervalFor,
  sessionPhase, shouldFlushNow, type SessionPhase,
} from "../lib/sessionExpiry";

// The five-minute warning before an Okta session ends (ENG-54731).
//
// A BANNER, not a modal. The session has five minutes left, not zero — the person
// may well want to finish the sentence they are typing, and a modal takes the
// keyboard away to tell them about it. It sits above the app rather than over it,
// and it is deliberately not dismissible: the countdown is the information, and a
// dismissed warning is indistinguishable from no warning at the moment it starts
// mattering.
//
// Rendered inside AuthGate's `ok` branch, so it appears on EVERY page — the editor,
// Review, Content, Assets — which is the criterion. A per-page banner would have
// been the same code copied into each surface, and it would have been missing from
// whichever one shipped next.

export default function SessionExpiryBanner() {
  const [expiresAt, setExpiresAt] = useState<number | null>(() => sessionExpiresAt());
  const [now, setNow] = useState(() => Date.now());
  const [extending, setExtending] = useState(false);
  // What the flush managed to save, once the session has actually ended. Null
  // until then. `false` is the case worth being loud about.
  const [flushed, setFlushed] = useState<boolean | null>(null);

  const phase: SessionPhase = sessionPhase(expiresAt, now);

  // Re-read the stored expiry as well as the clock: a refresh elsewhere (AuthGate's
  // own poll, or another tab) moves it, and a banner counting down against a stale
  // value would keep warning about a session that has already been extended.
  useEffect(() => {
    const tick = () => {
      setExpiresAt(sessionExpiresAt());
      setNow(Date.now());
    };
    tick();
    const id = setInterval(tick, pollIntervalFor(phase));
    // A laptop that slept comes back with a clock that jumped; recheck on focus
    // so the banner is right the moment it is looked at rather than up to a poll
    // later.
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [phase]);

  const left = msUntilExpiry(expiresAt, now);

  // Save the open work in the last seconds BEFORE expiry, without waiting for the
  // user to act. This is the criterion, and the timing is the whole of it: a save
  // issued after the token expires goes out with no valid credential and comes
  // back 401, so flushing on expiry would flush into a closed door.
  useEffect(() => {
    if (flushed !== null || !shouldFlushNow(left)) return;
    let cancelled = false;
    void (async () => {
      const ok = await flushBeforeExpiry();
      if (!cancelled) setFlushed(ok);
    })();
    return () => { cancelled = true; };
  }, [left, flushed]);

  // Expired. Anything open was saved a moment ago by the effect above; a tab that
  // was asleep through the whole window arrives here with flushed === null, so try
  // once more — it will probably 401, which is exactly what the message then says.
  useEffect(() => {
    if (phase !== "expired") return;
    let cancelled = false;
    void (async () => {
      const ok = flushed !== null ? flushed : await flushBeforeExpiry();
      if (cancelled) return;
      if (flushed === null) setFlushed(ok);
      // A failed save is NOT a reason to stay — the session is over either way and
      // the app can do nothing more. It is a reason to say so before leaving, so
      // give the message a beat to be read rather than redirecting mid-sentence.
      // (When the save failed, that flush has just parked a local copy of the work;
      // the editor offers it back when the page is reopened — devhub/lib/editorRescue.)
      setTimeout(() => { void reauthenticate(); }, ok ? 400 : 2500);
    })();
    return () => { cancelled = true; };
  }, [phase, flushed]);

  const onExtend = useCallback(async () => {
    setExtending(true);
    const ok = await extendSession();
    setExtending(false);
    if (ok) {
      setExpiresAt(sessionExpiresAt());
      setNow(Date.now());
      return;
    }
    // Couldn't renew in place. Save first, then go — the same order as an
    // unattended expiry, because the outcome for the user's work is the same.
    await flushBeforeExpiry();
    void reauthenticate();
  }, []);

  if (phase === "none" || phase === "ok") return null;

  if (phase === "expired") {
    return (
      <div className="session-banner expired" role="alert">
        <span className="session-banner-text">
          {/* A failed save now has a real second half to report: the flush parked a
              local copy, and reopening that page offers it back. Saying only "could
              not be saved" would send someone away believing the work is gone. */}
          {flushed === false
            ? "Your session ended and some changes could not be saved to the server. They're kept in this browser — reopen the page after signing in to restore them."
            : "Your session has ended. Your changes are saved. Taking you to sign in…"}
        </span>
      </div>
    );
  }

  const silent = canExtendSilently();
  const holdsWork = pendingFlushCount() > 0;
  return (
    <div className="session-banner" role="status" aria-live="polite">
      <span className="session-banner-text">
        Your session expires in <strong className="session-banner-clock">{formatCountdown(left ?? 0)}</strong>.
        {/* Only promised when something is actually registered to save. A blanket
            "your work is safe" on a page holding no work is a claim the app can't
            back, and the one time it matters it would already have been ignored. */}
        {holdsWork
          ? " Unsaved changes on this page will be saved automatically."
          : " Extend it to stay signed in."}
      </span>
      <button
        type="button"
        className="session-banner-btn"
        disabled={extending}
        onClick={() => void onExtend()}
      >
        {/* Labelled by what will actually happen. A button that says "Extend" and
            then navigates to Okta is exactly the abruptness this ticket is about. */}
        {extending ? "Extending…" : silent ? "Extend session" : "Sign in again"}
      </button>
    </div>
  );
}
